import {
  Activity,
  Archive,
  BarChart3,
  Calendar,
  CheckSquare,
  CreditCard,
  Download,
  Eye,
  FolderOpen,
  LayoutDashboard,
  Package,
  PhilippinePeso,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";

import type { User } from "../types";
import type { ViewType } from "../types/navigation";
import { canAccessSettings, userHasRole } from "./accessControl";

export interface SidebarNavigationItem {
  title: string;
  view: ViewType;
  icon: typeof LayoutDashboard;
}

export interface SidebarNavigationGroup {
  title: string;
  items: SidebarNavigationItem[];
}

export function buildSidebarNavigation(currentUser: User): SidebarNavigationGroup[] {
  const isManager = userHasRole(currentUser, "sysadmin", "executive", "director", "leader");
  const isSeniorManager = userHasRole(currentUser, "sysadmin", "executive");
  const isSystemViewer = userHasRole(currentUser, "sysadmin", "executive", "director");
  const isFabricator = userHasRole(currentUser, "fabricator");
  const isClient = userHasRole(currentUser, "client");
  const groups: SidebarNavigationGroup[] = [{
    title: "Overview",
    items: [{ title: "Dashboard", view: "dashboard", icon: LayoutDashboard }],
  }];

  const workspace: SidebarNavigationItem[] = [];
  if (isManager || isFabricator) {
    workspace.push(
      { title: "Projects", view: "projects", icon: FolderOpen },
      { title: "Tasks", view: "tasks", icon: CheckSquare },
    );
  }
  if (isFabricator) {
    workspace.push(
      { title: "Work Log", view: "worklog", icon: Calendar },
      { title: "Materials", view: "materials", icon: Package },
    );
  }
  if (isManager || isFabricator) {
    workspace.push({ title: "Feature Bank", view: "feature-bank", icon: Sparkles });
  }
  if (workspace.length) groups.push({ title: "Workspace", items: workspace });

  const organization: SidebarNavigationItem[] = [];
  if (isManager) organization.push({ title: "Team", view: "team", icon: Users });
  if (isSeniorManager) organization.push({ title: "Users", view: "users", icon: Users });
  if (organization.length) groups.push({ title: "Organization", items: organization });

  const insights: SidebarNavigationItem[] = [];
  if (isManager) insights.push({ title: "Reports", view: "reports", icon: BarChart3 });
  if (isSeniorManager) {
    insights.push(
      { title: "Revenue", view: "revenue", icon: PhilippinePeso },
      { title: "Billing", view: "billing", icon: CreditCard },
    );
  }
  if (insights.length) groups.push({ title: "Insights", items: insights });

  if (isClient) {
    groups.push({
      title: "My Project",
      items: [
        { title: "Project Status", view: "project-status", icon: Eye },
        { title: "Documentation", view: "documentation", icon: Download },
      ],
    });
  }

  const administration: SidebarNavigationItem[] = [];
  if (isSystemViewer) administration.push({ title: "Archives", view: "archives", icon: Archive });
  if (isSystemViewer) {
    administration.push({ title: "Activity Logs", view: "activity-logs", icon: Activity });
  }
  if (canAccessSettings(currentUser)) {
    administration.push({ title: "Settings", view: "settings", icon: Settings });
  }
  if (administration.length) groups.push({ title: "Administration", items: administration });

  return groups;
}
